import type { HTMLAttributes, MouseEventHandler } from "react";
import React, { useRef, useState } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { mandateFilterManager } from "../../../../../shared/core/service/services";
import { MandateStatus } from "../../../../../shared/domains/mandates/mandate";
import { mandateStatusLabels } from "../../../../../shared/domains/mandates/mandate-filter-labels";
import { useObservable } from "../../../../../shared/utils/observable";
import { useClickOutsideHandler } from "../../../../utils/use-outside-click";
import { TertiaryDiv } from "../../../common/buttons/tertiary-button";
import { ChevronDown } from "../../../common/svg/chevron-down";

interface FilterButtonProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  active?: boolean;
}

export const FilterDropdown = (props: FilterButtonProps) => {
  const { children, onClick, active, ...rest } = props;
  return (
    <FilterButtonContainer {...rest} onClick={onClick} $active={active}>
      {children}
      <ChevronDownIcon />
    </FilterButtonContainer>
  );
};

const PressableRow = styled.div`
  padding: 5px 10px 5px 10px;
  &:focus,
  &:active,
  &:hover {
    background-color: #e2e2e2;
    cursor: pointer;
  }
`;

export const MandatesStatusFilter = (props: HTMLAttributes<HTMLDivElement>) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownContentRef = useRef(null);
  useClickOutsideHandler(dropdownContentRef, () => setShowDropdown(false));
  const { formatMessage } = useIntl();
  const currentStatus = useObservable(mandateFilterManager.statusSelection);

  return (
    <FilterDropdown
      {...props}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowDropdown((show) => !show);
        }
      }}
      active={true}
    >
      {formatMessage(mandateStatusLabels[currentStatus])}
      <DropdownContent
        ref={dropdownContentRef}
        onClick={(e) => {
          e.stopPropagation();
        }}
        $isVisible={showDropdown}
      >
        {[
          MandateStatus.ALL,
          MandateStatus.ACTIVE,
          MandateStatus.CONSUMED,
          MandateStatus.CREDITOR_OPPOSED,
          MandateStatus.OBSOLETE,
          MandateStatus.OPPOSED,
          MandateStatus.REVOKED,
        ].map((status) => (
          <PressableRow
            key={status}
            onClick={() => {
              mandateFilterManager.setStatusSelection(status);
              setShowDropdown(false);
            }}
          >
            {formatMessage(mandateStatusLabels[status])}
          </PressableRow>
        ))}
      </DropdownContent>
    </FilterDropdown>
  );
};

const DropdownContent = styled.div<{ $isVisible: boolean }>`
  display: ${(props) => (props.$isVisible ? "block" : "none")};
  overflow: hidden;
  text-align: start;
  position: absolute;
  top: 120%;
  left: 0px;
  background-color: #f4f4f4;
  border-radius: 10px;
  z-index: 1;
  min-width: 100%;
  &:hover {
    cursor: auto;
  }
`;

const FilterButtonContainer = styled(TertiaryDiv)<{ $active?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background-color: ${(props) => (props.$active ? "#e4e4e4" : "#f4f4f4")};
  &:hover {
    cursor: pointer;
  }
`;

const ChevronDownIcon = styled(ChevronDown)`
  margin-left: 6px;
  margin-right: 6px;
  pointer-events: none;
  width: 15px;
`;
